import { toKebabCaseWithAccentsCyrillicCJK } from 'corporate-utils';
import { defaultInternationalization, Languages } from 'corporate-types';

// Models
import { Category, MegamenuLink } from '../models/domain/interfaces';

// Services
import {
  getCategoriesByParentCategoryId,
  getProductByIdAndCheckCategoryId,
} from '../services/dwh';

// Utils
import { getAvailableStringFromCategoryByLocale } from './categories';
import { Logger } from 'corporate-utils';
import { SubcategorySeo as SubcategorySeoStrapi } from '../models/auto-generated-strapi-types/types/components/SubcategorySeo';

const PRINT_FROM = false;

export interface CheckUtilsType {
  result: boolean;
  supportInfo?: {
    [key: string]: any;
  };
}

export const checkIfSlugIsCategoryPage = async (
  slug: string[],
  lang: string,
  locale: string,
  megamenuLinks: MegamenuLink[],
  query?: any,
  translations?: {
    [key: string]: any;
  }
): Promise<CheckUtilsType> => {
  if (Array.isArray(slug) && megamenuLinks && megamenuLinks.length) {
    const [categoryFather, categoryChild] = slug;

    const categoryFatherFound = megamenuLinks.find?.((link) => {
      return (
        // Li controlla dal mega menu e non dalla categoria
        // Quindi:
        // - category deve coincidere con lo slug del megamenu e categoryFather (il primo parametro dello slug)
        // - categoty father deve coincidere con il campo category del CMS
        link.slug &&
        (
          toKebabCaseWithAccentsCyrillicCJK(link?.slug || '') as string
        )?.toLowerCase() === categoryFather
      );
    });

    if (categoryFatherFound) {
      const categoryChildFound = categoryFatherFound.firstChild?.find?.(
        (category) => {
          return (
            (Object.values(Languages) as string[]).indexOf(lang) !== -1 &&
            (
              (toKebabCaseWithAccentsCyrillicCJK(
                category &&
                  getAvailableStringFromCategoryByLocale(
                    category,
                    lang,
                    'packages/corporate-ui/src/utils/urls.ts'
                  )
              ) as string) || ''
            )?.toLowerCase() === categoryChild
          );
        }
      );

      if (query?.[translations?.queryParmas?.subcategory]) {
        if (categoryChildFound) {
          const categoriesChild = await getCategoriesByParentCategoryId(
            String(categoryChildFound?.id),
            true,
            locale
          );

          const seoInfoOfSubcategory =
            categoryChildFound?.cmsInfo?.attributes?.subcategorySeo?.find(
              (seoInfo: SubcategorySeoStrapi) => {
                return (
                  ~~seoInfo.idDwh ===
                  ~~query?.[translations?.queryParmas?.subcategory]
                );
              }
            );

          const subcategoryFound = categoriesChild.find?.(
            (c: any) =>
              String(c.id) === query?.[translations?.queryParmas?.subcategory]
          );

          if (subcategoryFound) {
            const result = {
              result: !!categoryChildFound,
              supportInfo: {
                categoryFatherFound,
                categoryChildFound,
                seoInfoOfSubcategory,
              },
            };

            if (subcategoryFound) {
              (result?.supportInfo as any).subcategoryFound = subcategoryFound;
            }

            return result;
          } else {
            // if there is a subcategory, but it does not appear in the children of the second category -> error
            return {
              result: false,
            };
          }
        } else {
          // if there is a subcategory, but no second category -> error
          return {
            result: false,
          };
        }
      }

      return {
        result: !!categoryChildFound,
        supportInfo: {
          categoryFatherFound,
          categoryChildFound,
        },
      };
    } else {
      return {
        result: false,
      };
    }
  } else {
    return {
      result: false,
    };
  }
};

export const checkIfSlugIsProductPage = async (
  slug: string[],
  lang: string,
  locale: string,
  megamenuLinks: MegamenuLink[],
  internationalizationLocale?: string
): Promise<CheckUtilsType> => {
  if (Array.isArray(slug) && megamenuLinks && megamenuLinks.length) {
    const [categoryFather, categoryChild, productName, productId] = slug;

    const { result: resultCheckIfSlugIsCategoryPage, supportInfo } =
      await checkIfSlugIsCategoryPage(slug, lang, locale, megamenuLinks);

    if (resultCheckIfSlugIsCategoryPage) {
      const product = await getProductByIdAndCheckCategoryId(
        productId,
        supportInfo?.categoryChildFound.id,
        lang,
        internationalizationLocale
      );

      let categoryFatherFound = false;
      let categoryChildFound = false;

      product?.categoriesCalculatedInfo?.forEach(
        (categoriesCalculatedInfo: any[]) => {
          const found = categoriesCalculatedInfo.find((category: Category) => {
            return (
              (Object.values(Languages) as string[]).indexOf(lang) !== -1 &&
              (
                (toKebabCaseWithAccentsCyrillicCJK(
                  category &&
                    getAvailableStringFromCategoryByLocale(
                      category,
                      lang === 'zh' ? 'cn' : lang,
                      'packages/corporate-ui/src/utils/urls.ts'
                    )
                ) as string) || ''
              )?.toLowerCase() === categoryFather
            );
          });

          if (found) {
            categoryFatherFound = true;
          }
        }
      );

      product?.categoriesCalculatedInfo?.forEach(
        (categoriesCalculatedInfo: any[]) => {
          const found = categoriesCalculatedInfo.find((category: Category) => {
            return (
              (Object.values(Languages) as string[]).indexOf(lang) !== -1 &&
              (
                (toKebabCaseWithAccentsCyrillicCJK(
                  category &&
                    getAvailableStringFromCategoryByLocale(
                      category,
                      lang === 'zh' ? 'cn' : lang,
                      'packages/corporate-ui/src/utils/urls.ts'
                    )
                ) as string) || ''
              )?.toLowerCase() === categoryChild
            );
          });

          if (found) {
            categoryChildFound = true;
          }
        }
      );

      if (
        product &&
        !product?.error &&
        categoryFatherFound &&
        categoryChildFound
      ) {
        return {
          result:
            (
              (toKebabCaseWithAccentsCyrillicCJK(
                product?.nameFather || ''
              ) as string) || ''
            )?.toLowerCase() === decodeURIComponent(productName),
          supportInfo: {
            ...supportInfo,
            product,
          },
        };
      } else {
        return {
          result: false,
        };
      }
    } else {
      return {
        result: false,
      };
    }
  } else {
    return {
      result: false,
    };
  }
};

export function urlFromCategoriesIds(
  megamenuLinks: any,
  categoriesCalculated: number[] | undefined,
  locale = defaultInternationalization,
  type: string,
  product?: any,
  from?: string
) {
  if (from && PRINT_FROM) {
    Logger.info(from);
  }

  const parent0 = categoriesCalculated && categoriesCalculated[0];
  const parent1 = categoriesCalculated && categoriesCalculated[1];

  const categoryIndex = megamenuLinks?.findIndex(
    (e: any) => parseInt(e.idDwh) == parent0
  );

  if (categoryIndex !== -1) {
    const categoryFatherIndex = megamenuLinks?.[
      categoryIndex
    ].firstChild?.findIndex((e: any) => e.depth == 0);

    const categoryText =
      megamenuLinks?.[categoryIndex]?.firstChild?.[categoryFatherIndex] &&
      getAvailableStringFromCategoryByLocale(
        megamenuLinks[categoryIndex].firstChild[categoryFatherIndex],
        locale,
        'packages/corporate-ui/src/utils/urls.ts 1'
      );

    const subCategoryIndex = megamenuLinks?.[
      categoryIndex
    ].firstChild?.findIndex(
      (e: any) => parseInt(e.id) == parent1 && e.depth == 1
    );

    if (subCategoryIndex != -1) {
      const subCategoryText =
        megamenuLinks?.[categoryIndex]?.firstChild?.[subCategoryIndex] &&
        getAvailableStringFromCategoryByLocale(
          megamenuLinks[categoryIndex].firstChild[subCategoryIndex],
          locale,
          'packages/corporate-ui/src/utils/urls.ts 2'
        );

      if (subCategoryText) {
        const url = (
          toKebabCaseWithAccentsCyrillicCJK(categoryText) +
          '/' +
          toKebabCaseWithAccentsCyrillicCJK(subCategoryText)
        )
          ?.toLowerCase()
          ?.replace(/\s/g, '-');

        if (type === 'product') {
          return `/${url}/${encodeURIComponent(
            (toKebabCaseWithAccentsCyrillicCJK(
              product?.nameFather || ''
            ) as string) || ''
          )}/${encodeURIComponent(product?.sku)}`;
        } else return '/' + url;
      }
    } else if (type === 'category_father') {
      const url = toKebabCaseWithAccentsCyrillicCJK(categoryText)
        ?.toLowerCase()
        ?.replace(/\s/g, '-');
      return '/' + url;
    }
  }
  return null;
}
